import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import {
  useTrackTypes,
  useCreateTrackType,
  useUpdateTrackType,
  useReorderTrackTypes,
} from '../hooks/useOps';
import type { TrackTypeDef } from '../api/types';

const EMPTY = { label: '', code: '', defaultUnitLabel: 'pcs' };

export function TrackTypesPage() {
  // Admin view shows archived rows too (greyed, toggle off to un-archive).
  const { data: rows = [], isLoading, error } = useTrackTypes(true);
  const create = useCreateTrackType();
  const update = useUpdateTrackType();
  const reorder = useReorderTrackTypes();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TrackTypeDef | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<TrackTypeDef | null>(null);

  // Canonical order: sort_order ASC, then label. Drives the reorder arrows too.
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
    [rows],
  );

  const labelCollision = useMemo(() => {
    const trimmed = form.label.trim().toLowerCase();
    if (!trimmed) return false;
    return rows.some((r) => r.label.trim().toLowerCase() === trimmed && r.id !== editing?.id);
  }, [form.label, rows, editing]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  };
  const openEdit = (t: TrackTypeDef) => {
    setEditing(t);
    setForm({ label: t.label, code: t.code, defaultUnitLabel: t.defaultUnitLabel });
    setFormError(null);
    setOpen(true);
  };

  const submit = async () => {
    setFormError(null);
    const label = form.label.trim();
    const defaultUnitLabel = form.defaultUnitLabel.trim() || 'pcs';
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, body: { label, defaultUnitLabel } });
        setToast(`Saved "${label}"`);
      } else {
        await create.mutateAsync({
          label,
          code: form.code.trim() || undefined,
          defaultUnitLabel,
        });
        setToast(`Added "${label}"`);
      }
      setOpen(false);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const move = (index: number, dir: -1 | 1) => {
    const next = [...sorted];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorder.mutate(next.map((r) => r.id));
  };

  // Active on->off opens the archive confirm; off->on un-archives immediately.
  const onToggleActive = (t: TrackTypeDef) => {
    if (t.active) setArchiveTarget(t);
    else update.mutate({ id: t.id, body: { active: true } }, { onSuccess: () => setToast(`Restored "${t.label}"`) });
  };

  const confirmArchive = () => {
    if (!archiveTarget) return;
    const t = archiveTarget;
    update.mutate(
      { id: t.id, body: { active: false } },
      { onSuccess: () => setToast(`Archived "${t.label}"`) },
    );
    setArchiveTarget(null);
  };

  const columns: GridColDef<TrackTypeDef>[] = [
    { field: 'label', headerName: 'Label', minWidth: 180, flex: 1 },
    {
      field: 'code',
      headerName: 'Code',
      width: 150,
      renderCell: (p) => <Chip size="small" label={p.value} variant="outlined" sx={{ fontFamily: 'monospace' }} />,
    },
    { field: 'defaultUnitLabel', headerName: 'Default unit', width: 120 },
    { field: 'usageCount', headerName: 'In use', width: 90, type: 'number' },
    {
      field: 'sortOrder',
      headerName: 'Order',
      width: 130,
      sortable: false,
      renderCell: (p) => {
        const index = sorted.findIndex((r) => r.id === p.row.id);
        return (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <IconButton
              size="small"
              aria-label={`Move ${p.row.label} up`}
              disabled={index <= 0 || reorder.isPending}
              onClick={() => move(index, -1)}
            >
              <ArrowUpwardIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label={`Move ${p.row.label} down`}
              disabled={index >= sorted.length - 1 || reorder.isPending}
              onClick={() => move(index, 1)}
            >
              <ArrowDownwardIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      },
    },
    {
      field: 'active',
      headerName: 'Active',
      width: 100,
      sortable: false,
      renderCell: (p) => (
        <Switch
          size="small"
          checked={p.row.active}
          onChange={() => onToggleActive(p.row)}
          inputProps={{ 'aria-label': `${p.row.active ? 'Archive' : 'Restore'} ${p.row.label}` }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Button size="small" onClick={() => openEdit(p.row)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Track Types"
        subtitle="The catalog of track categories available in project setup"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add type
          </Button>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Codes are permanent — only <strong>labels</strong> change. Existing tracks reference a
        type by its code, so renaming is display-only and removal is <strong>archive</strong>, never
        delete. Archived types stay off new setups but never touch existing tracks.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ height: 560 }}>
        <DataGrid
          rows={sorted}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          disableColumnMenu
          getRowClassName={(p) => (p.row.active ? '' : 'row-archived')}
          sx={{ '& .row-archived': { opacity: 0.5 } }}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.label}` : 'Add track type'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              fullWidth
              autoFocus
              helperText={labelCollision ? 'Another type already uses this label — allowed, but they look identical.' : ' '}
              error={labelCollision}
            />
            <Tooltip title={editing ? 'Codes are immutable — live tracks reference them.' : 'Leave blank to auto-generate from the label.'}>
              <TextField
                label="Code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                fullWidth
                disabled={!!editing}
                placeholder="auto from label"
                inputProps={{ maxLength: 20 }}
              />
            </Tooltip>
            <TextField
              label="Default unit"
              value={form.defaultUnitLabel}
              onChange={(e) => setForm({ ...form, defaultUnitLabel: e.target.value })}
              fullWidth
              helperText="Prefills the Unit field when a track of this type is created."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={create.isPending || update.isPending || form.label.trim().length < 1}
            onClick={submit}
          >
            {editing ? 'Save' : 'Add type'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!archiveTarget} onClose={() => setArchiveTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Archive "{archiveTarget?.label}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Used by {archiveTarget?.usageCount ?? 0} active track
            {archiveTarget?.usageCount === 1 ? '' : 's'}. Archiving hides it from <strong>new</strong>{' '}
            setups only; existing tracks are unaffected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmArchive}>
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
